import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  runTransaction,
  Timestamp,
  limit,
  orderBy,
  deleteDoc,
} from "firebase/firestore";
import { db, storage, auth } from "./firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { cleanText, cleanEmail, cleanPhone, cleanInt, cleanNumber, FIELD_CAPS } from "./sanitize";

// -------- USERS --------
const TRIAL_DAYS = 15;
const SUBSCRIPTION_PRICE = 499;

export function getSubscriptionState(user) {
  if (!user) return { active: true, plan: "free", status: "free", needsUpgrade: false };
  if (user.role !== "coach") return { active: true, plan: "free", status: "free", needsUpgrade: false };

  const now = Date.now();
  const status = user.subscriptionStatus;

  if (!status || status === "free") {
    return { active: true, plan: "free", status: "free", onTrial: false, needsUpgrade: false, daysLeft: null };
  }

  const adminManaged = user.adminManaged === true;

  if (status === "active") {
    const expiresAt = typeof user.currentPeriodEndsAt === "number"
      ? user.currentPeriodEndsAt
      : typeof user.nextBillingDate === "number"
      ? user.nextBillingDate
      : 0;
    return {
      active: true, plan: "pro", onTrial: false,
      daysLeft: expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / 86400000)) : null,
      needsUpgrade: false, status: "active",
      nextBillingDate: expiresAt || user.nextBillingDate,
      subscriptionType: user.planType || user.subscriptionType,
      adminManaged,
    };
  }

  if (status === "trial") {
    // Use explicit trialEndsAt if stored (upgrade path preserves it),
    // otherwise fall back to trialStartDate + TRIAL_DAYS
    const trialStartedAt = typeof user.trialStartDate === "number"
      ? user.trialStartDate
      : typeof user.trialStartedAt === "number"
      ? user.trialStartedAt
      : now;
    const trialEndsAt = typeof user.trialEndsAt === "number"
      ? user.trialEndsAt
      : trialStartedAt + TRIAL_DAYS * 86400000;
    const daysLeft = Math.max(0, Math.ceil((trialEndsAt - now) / 86400000));
    if (now < trialEndsAt) {
      return {
        active: true, plan: "trial", onTrial: true, daysLeft, trialEndsAt,
        needsUpgrade: false, status: "trial",
        subscriptionType: user.planType || user.subscriptionType,
        nextBillingDate: trialEndsAt,
        adminManaged,
      };
    }
    return { active: true, plan: "free", onTrial: false, daysLeft: null, needsUpgrade: false, status: "free" };
  }

  // past_due: payment failed — 5-day grace period before access is suspended
  if (status === "past_due") {
    const gracePeriodEndsAt = typeof user.gracePeriodEndsAt === "number" ? user.gracePeriodEndsAt : 0;
    if (gracePeriodEndsAt > now) {
      return {
        active: true, plan: "pro", status: "past_due",
        daysUntilLock: Math.max(0, Math.ceil((gracePeriodEndsAt - now) / 86400000)),
        gracePeriodEndsAt,
        subscriptionType: user.planType || user.subscriptionType,
        needsUpgrade: false,
        adminManaged,
      };
    }
    // Grace period expired — suspend access
    return { active: false, plan: "free", status: "free", needsUpgrade: false };
  }

  if (status === "payment_failed") {
    // Legacy: treat old payment_failed same as past_due with no grace remaining
    return { active: false, plan: "free", onTrial: false, daysLeft: null, needsUpgrade: false, status: "free" };
  }

  if (status === "canceled") {
    const expiresAt = typeof user.nextBillingDate === "number" ? user.nextBillingDate : 0;
    if (expiresAt && expiresAt > now) {
      return {
        active: true, plan: "pro", onTrial: false,
        daysLeft: Math.max(0, Math.ceil((expiresAt - now) / 86400000)),
        needsUpgrade: false, status: "canceled",
        nextBillingDate: user.nextBillingDate,
        adminManaged,
      };
    }
    return { active: true, plan: "free", onTrial: false, daysLeft: null, needsUpgrade: false, status: "free" };
  }

  return { active: true, plan: "free", onTrial: false, daysLeft: null, needsUpgrade: false, status: "free" };
}

export { TRIAL_DAYS, SUBSCRIPTION_PRICE };

export async function createUserProfile(uid, data) {
  // Whitelist + length-cap the fields a client may write at signup.
  // Anything else server-side (subscriptionStatus, plan, organizationId, etc.)
  // is set by Cloud Functions or by the rules-protected updates below.
  const patch = {};
  if (data.email !== undefined) patch.email = cleanEmail(data.email);
  if (data.name !== undefined) patch.name = cleanText(data.name, FIELD_CAPS.name);
  if (data.role !== undefined) {
    const role = String(data.role).toLowerCase();
    if (!["coach", "client", "admin"].includes(role)) {
      throw new Error("Invalid role");
    }
    patch.role = role;
  }
  if (data.createdAt !== undefined) patch.createdAt = Number(data.createdAt) || Date.now();
  if (data.subscriptionStatus !== undefined) patch.subscriptionStatus = cleanText(data.subscriptionStatus, 24);
  if (data.plan !== undefined) patch.plan = cleanText(data.plan, 24);
  if (data.inviteCode !== undefined) patch.inviteCode = cleanText(data.inviteCode, FIELD_CAPS.inviteCode);
  if (data.coachSource !== undefined) patch.coachSource = cleanText(data.coachSource, 24);
  if (data.onboardingType !== undefined) patch.onboardingType = cleanText(data.onboardingType, 24);
  if (data.linkedCoachId !== undefined) patch.linkedCoachId = cleanText(data.linkedCoachId, 128);
  if (data.country !== undefined) patch.country = cleanText(data.country, 2);
  if (data.phone !== undefined) patch.phone = cleanPhone(data.phone);
  await setDoc(doc(db, "users", uid), patch, { merge: true });
}

export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? { uid, ...snap.data() } : null;
}

// ── WhatsApp templates — stored on the coach's user doc ──────────────────────
export async function getWhatsAppTemplates(coachId) {
  const snap = await getDoc(doc(db, "users", coachId));
  return snap.data()?.whatsappTemplates || {};
}

export async function saveWhatsAppTemplate(coachId, category, message) {
  const cleanCategory = cleanText(category, 40);
  const cleanMsg = cleanText(message, FIELD_CAPS.message);
  if (!cleanCategory) throw new Error("Category is required");
  if (!cleanMsg) throw new Error("Template message cannot be empty");
  const snap = await getDoc(doc(db, "users", coachId));
  const existing = snap.data()?.whatsappTemplates || {};
  const list = existing[cleanCategory] || [];
  if (list.includes(cleanMsg)) return; // no duplicates
  if (list.length >= 50) throw new Error("Too many templates in this category");
  await setDoc(doc(db, "users", coachId), {
    whatsappTemplates: { ...existing, [cleanCategory]: [...list, cleanMsg] },
  }, { merge: true });
}

export async function deleteWhatsAppTemplate(coachId, category, index) {
  const snap = await getDoc(doc(db, "users", coachId));
  const existing = snap.data()?.whatsappTemplates || {};
  const list = [...(existing[category] || [])];
  list.splice(index, 1);
  await setDoc(doc(db, "users", coachId), {
    whatsappTemplates: { ...existing, [category]: list },
  }, { merge: true });
}

export async function linkClientByEmail(uid, email) {
  const q = query(collection(db, "clients"), where("email", "==", email.toLowerCase()));
  const snaps = await getDocs(q);
  if (snaps.empty) return null;
  const clientDoc = snaps.docs[0];
  await setDoc(doc(db, "users", uid), { clientId: clientDoc.id }, { merge: true });
  return clientDoc.id;
}

// NOTE: backfillClientLinksForCoach moved to a Cloud Function
// (functions/index.js → backfillCoachClientLinks) because the tightened
// /users rules no longer allow client-side cross-tenant email lookups.
// Use backfillCoachClientLinksFn from ./functions instead.

// -------- CLIENT LIMIT CHECK --------
export async function getActiveClientCount(coachId) {
  const q = query(
    collection(db, "clients"),
    where("coachId", "==", coachId),
    where("deleted", "==", false)
  );
  const snap = await getDocs(q);
  return snap.size;
}

// -------- CLIENTS --------
export async function addClient(coachId, payload) {
  const name = cleanText(payload.name, FIELD_CAPS.name);
  if (!name) throw new Error("Client name is required");
  const currency = payload.currency === "USD" ? "USD" : "INR";
  const ref = await addDoc(collection(db, "clients"), {
    coachId,
    organizationId: payload.organizationId || null,
    name,
    phone: cleanPhone(payload.phone),
    email: cleanEmail(payload.email),
    totalSessions: cleanInt(payload.totalSessions, { min: 0, max: 10000 }),
    sessionsUsed: 0,
    amountPaid: cleanNumber(payload.amountPaid, { min: 0, max: 10_000_000 }),
    currency, // locked at create time — drives every display of this client's money
    status: "active",
    deleted: false,
    coachingType: ["live", "self_tracking"].includes(payload.coachingType) ? payload.coachingType : "live",
    // Coaching time slot — days array + 24h time string
    slotDays: Array.isArray(payload.slotDays) ? payload.slotDays.slice(0, 7) : [],
    slotTime: cleanText(payload.slotTime, 16),
    createdAt: serverTimestamp(),
    // Store userId directly if passed — avoids needing to query users collection
    ...(payload.userId && { userId: cleanText(payload.userId, 128) }),
  });
  return ref.id;
}

export async function updateClientSlot(clientId, { slotDays, slotTime }) {
  await updateDoc(doc(db, "clients", clientId), { slotDays, slotTime });
}

export async function setClientStatus(clientId, status) {
  await updateDoc(doc(db, "clients", clientId), { status });
}

export async function softDeleteClient(clientId) {
  await updateDoc(doc(db, "clients", clientId), { deleted: true, status: "inactive" });
}

export async function updateClientPackage(clientId, { totalSessions, amountPaid, currency }) {
  const patch = {};
  if (totalSessions !== undefined) patch.totalSessions = cleanInt(totalSessions, { min: 0, max: 10000 });
  if (amountPaid !== undefined) patch.amountPaid = cleanNumber(amountPaid, { min: 0, max: 10_000_000 });
  // If the caller passes a currency (i.e. coach edits a money field) we stamp it.
  // Existing docs without a currency stay legacy (defaults to INR at read time).
  if (currency === "USD" || currency === "INR") patch.currency = currency;
  await updateDoc(doc(db, "clients", clientId), patch);
}

export function subscribeClients(coachId, cb) {
  const q = query(collection(db, "clients"), where("coachId", "==", coachId));
  return onSnapshot(q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      cb(rows);
    },
    (err) => console.error("❌ subscribeClients:", err.code)
  );
}

export function subscribeClient(clientId, cb) {
  return onSnapshot(doc(db, "clients", clientId),
    (snap) => { cb(snap.exists() ? { id: snap.id, ...snap.data() } : null); },
    (err) => console.error("❌ subscribeClient:", err.code)
  );
}

// -------- SESSIONS --------
export function subscribeSessions(clientId, cb, coachId = null) {
  const q = coachId
    ? query(collection(db, "sessions"), where("clientId", "==", clientId), where("coachId", "==", coachId), limit(200))
    : query(collection(db, "sessions"), where("clientId", "==", clientId), limit(200));

  return onSnapshot(q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const ta = a.dateTime?.toMillis ? a.dateTime.toMillis() : 0;
        const tb = b.dateTime?.toMillis ? b.dateTime.toMillis() : 0;
        return tb - ta;
      });
      cb(rows);
    },
    (err) => console.error("❌ subscribeSessions:", err.code)
  );
}

export async function logSession(coachId, clientId, notes = "", status = "attended", dateOverride = null, countSession = true) {
  return await runTransaction(db, async (tx) => {
    const clientRef = doc(db, "clients", clientId);
    const clientSnap = await tx.get(clientRef);
    if (!clientSnap.exists()) throw new Error("Client not found");
    const c = clientSnap.data();
    if (c.deleted) throw new Error("Client is deleted");
    if (c.status === "inactive") throw new Error("Client is inactive. Reactivate to log sessions.");

    // Only validate + deduct sessions when counting
    if (countSession) {
      const remaining = (c.totalSessions || 0) - (c.sessionsUsed || 0);
      if (remaining <= 0) throw new Error("No sessions remaining");
      tx.update(clientRef, { sessionsUsed: (c.sessionsUsed || 0) + 1 });
    }

    const sessionRef = doc(collection(db, "sessions"));
    const cleanStatus = ["attended", "cancelled", "missed", "rescheduled"].includes(status)
      ? status
      : "attended";
    tx.set(sessionRef, {
      clientId, coachId,
      dateTime: dateOverride ? Timestamp.fromDate(dateOverride) : Timestamp.now(),
      notes: cleanText(notes, FIELD_CAPS.notes),
      status: cleanStatus,
      counted: Boolean(countSession),
      coachConfirmed: true,
      clientConfirmed: false,
      createdAt: serverTimestamp(),
    });
    return sessionRef.id;
  });
}

export async function confirmSession(sessionId, role) {
  // NOTE: sessionsUsed is already incremented by logSession() at the time
  // the coach logs the session. confirmSession() must NOT increment it again
  // or every client-confirmed session gets double-counted.
  return await runTransaction(db, async (tx) => {
    const sRef = doc(db, "sessions", sessionId);
    const sSnap = await tx.get(sRef);
    if (!sSnap.exists()) throw new Error("Session not found");
    const s = sSnap.data();
    const myField = role === "coach" ? "coachConfirmed" : "clientConfirmed";
    if (!s[myField]) {
      tx.update(sRef, { [myField]: true });
    }
  });
}

// Recalculates sessionsUsed from actual session documents.
// Runs silently in the background when a client is selected — self-heals
// any accounts where the old double-counting bug inflated the counter.
export async function fixSessionsUsed(clientId, coachId) {
  try {
    const q = query(
      collection(db, "sessions"),
      where("clientId", "==", clientId),
      where("coachId", "==", coachId),
      where("coachConfirmed", "==", true)
    );
    const [sessSnap, clientSnap] = await Promise.all([
      getDocs(q),
      getDoc(doc(db, "clients", clientId)),
    ]);
    if (!clientSnap.exists()) return;
    // Only count sessions where counted !== false (excludes uncounted cancels)
    const actualCount = sessSnap.docs.filter((d) => d.data().counted !== false).length;
    const stored = clientSnap.data().sessionsUsed || 0;
    if (stored !== actualCount) {
      await updateDoc(doc(db, "clients", clientId), { sessionsUsed: actualCount });
    }
  } catch {}
}

// -------- DEMO CLEANUP --------
// Demo clients (Alex/Jordan/Sam) used to be auto-seeded for new coaches.
// Seeding is gone; this removes any leftovers plus their sessions.
const DEMO_EMAILS = ["client@demo.com", "jordan@demo.com", "sam@demo.com"];

export async function cleanupDemoClients(coachId) {
  const snap = await getDocs(query(collection(db, "clients"), where("coachId", "==", coachId)));
  const demoDocs = snap.docs.filter((d) => DEMO_EMAILS.includes((d.data().email || "").toLowerCase()));
  for (const clientDoc of demoDocs) {
    const sessions = await getDocs(query(collection(db, "sessions"), where("clientId", "==", clientDoc.id)));
    for (const s of sessions.docs) await deleteDoc(s.ref);
    await deleteDoc(clientDoc.ref);
  }
  return demoDocs.length;
}

// -------- SESSION RENEWAL --------
export async function renewClientSessions(clientId, {
  sessionsToAdd,
  packageName,
  amountPaid,     // number, optional
  expiryDate,     // ISO date string | null
  paymentDate,    // ISO date string | null
  planStartDate,  // ISO date string | null — which month to attribute sessions to
  paymentStatus,  // "paid" | "partial" | "pending"
  paymentMode,    // "upi" | "cash" | "bank" | "other"
  notes,          // string
  coachId,        // string
  currency,       // "INR" | "USD"
}) {
  const clientRef = doc(db, "clients", clientId);
  const snap = await getDoc(clientRef);
  if (!snap.exists()) throw new Error("Client not found");
  const c = snap.data();
  const newTotal = (c.totalSessions || 0) + sessionsToAdd;

  const lockedCurrency = currency === "USD" ? "USD" : "INR";

  // Build history entry (serverTimestamp can't go inside arrays — use ISO string)
  const entry = {
    id: `renewal_${Date.now()}`,
    createdAt: new Date().toISOString(),
    sessionsAdded: sessionsToAdd,
    packageName: packageName || "",
    amountPaid: Number(amountPaid) || 0,
    currency: lockedCurrency,
    expiryDate: expiryDate || null,
    paymentDate: paymentDate || null,
    planStartDate: planStartDate || null,
    paymentStatus: paymentStatus || "paid",
    paymentMode: paymentMode || "cash",
    notes: notes || "",
    renewedBy: coachId || "",
  };

  const patch = {
    totalSessions: newTotal,
    renewalHistory: [...(c.renewalHistory || []), entry],
    lastRenewedAt: serverTimestamp(),
    // Stamp the live currency on the client doc — used as the fallback
    // for any future read that doesn't have a per-entry currency.
    currency: lockedCurrency,
    // keep legacy packages array for backward compat
    packages: [...(c.packages || []), {
      date: new Date().toISOString(),
      sessions: sessionsToAdd,
      packageName: packageName || "",
    }],
    // current package snapshot (overwritten each renewal)
    currentPackageName: packageName || c.currentPackageName || "",
    currentExpiryDate: expiryDate || null,
    currentPaymentDate: paymentDate || null,
    currentPlanStartDate: planStartDate || null,
    currentPaymentStatus: paymentStatus || "paid",
    currentPaymentMode: paymentMode || "cash",
  };

  if (Number(amountPaid) > 0) {
    patch.amountPaid = (c.amountPaid || 0) + Number(amountPaid);
  }

  await updateDoc(clientRef, patch);
}

// -------- EDIT CLIENT --------
export async function editClient(clientId, { name, phone }) {
  const cleanName = cleanText(name, FIELD_CAPS.name);
  if (!cleanName) throw new Error("Name is required");
  await updateDoc(doc(db, "clients", clientId), {
    name: cleanName,
    phone: cleanPhone(phone),
  });
}

// -------- COACH PROFILE --------
export async function updateCoachProfile(uid, profileData) {
  // Whitelist + length-cap fields written into the coach's `profile` map.
  const p = profileData || {};
  const cleaned = {};
  if (p.name !== undefined)         cleaned.name = cleanText(p.name, FIELD_CAPS.name);
  if (p.bio !== undefined)          cleaned.bio = cleanText(p.bio, 1000);
  if (p.phone !== undefined)        cleaned.phone = cleanPhone(p.phone);
  if (p.email !== undefined)        cleaned.email = cleanEmail(p.email);
  if (p.specialties !== undefined)  cleaned.specialties = cleanText(p.specialties, 500);
  if (p.experience !== undefined)   cleaned.experience = cleanText(p.experience, 500);
  if (p.location !== undefined)     cleaned.location = cleanText(p.location, 200);
  if (p.photoURL !== undefined)     cleaned.photoURL = cleanText(p.photoURL, FIELD_CAPS.url);
  if (p.instagram !== undefined)    cleaned.instagram = cleanText(p.instagram, 100);
  if (p.website !== undefined)      cleaned.website = cleanText(p.website, FIELD_CAPS.url);
  await setDoc(doc(db, "users", uid), { profile: cleaned }, { merge: true });
}

export async function getCoachProfileByClientId(clientId) {
  const snap = await getDoc(doc(db, "clients", clientId));
  if (!snap.exists()) return null;
  const coachId = snap.data().coachId;
  if (!coachId) return null;
  const coachSnap = await getDoc(doc(db, "users", coachId));
  return coachSnap.exists() ? { uid: coachId, ...coachSnap.data() } : null;
}

// -------- LEADERBOARD --------
export function subscribeLeaderboard(coachId, cb) {
  // Use a single where clause to avoid composite index requirements.
  // leaderboardOptIn and deleted are filtered in JS.
  const q = query(
    collection(db, "clients"),
    where("coachId", "==", coachId),
  );
  return onSnapshot(q,
    (snap) => {
      const rows = snap.docs
        .filter((d) => d.data().leaderboardOptIn === true && !d.data().deleted)
        .map((d) => ({ id: d.id, ...d.data() }));
      cb(rows);
    },
    (err) => {
      console.error("subscribeLeaderboard error:", err);
      cb([]);
    }
  );
}

export async function updateClientLeaderboardProfile(clientId, { leaderboardName, leaderboardOptIn }) {
  await updateDoc(doc(db, "clients", clientId), {
    leaderboardName: cleanText(leaderboardName, FIELD_CAPS.leaderboardName),
    leaderboardOptIn: Boolean(leaderboardOptIn),
  });
}

// -------- CLIENT NOTIFICATIONS --------
export async function sendClientNotification(clientId, coachId, message) {
  const cleanMsg = cleanText(message, FIELD_CAPS.message);
  if (!cleanMsg) throw new Error("Message cannot be empty");
  await addDoc(collection(db, "clientNotifications"), {
    clientId,
    coachId,
    message: cleanMsg,
    read: false,
    createdAt: serverTimestamp(),
    type: "coach_message",
  });
}

export function subscribeClientNotifications(clientId, cb) {
  const q = query(
    collection(db, "clientNotifications"),
    where("clientId", "==", clientId),
    limit(50)
  );
  return onSnapshot(q,
    (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return tb - ta;
        });
      cb(rows);
    },
    (err) => { console.error("subscribeClientNotifications error:", err); cb([]); }
  );
}

export async function markClientNotificationsRead(notifIds) {
  if (!notifIds?.length) return;
  await Promise.all(
    notifIds.map((id) => updateDoc(doc(db, "clientNotifications", id), { read: true }))
  );
}

export async function updateClientProfile(clientId, data) {
  const patch = {};
  if (data.name !== undefined)            patch.name = cleanText(data.name, FIELD_CAPS.name);
  if (data.gender !== undefined)          patch.gender = cleanText(data.gender, 20);
  if (data.age !== undefined)             patch.age = cleanInt(data.age, { min: 0, max: 130 });
  if (data.weight !== undefined)          patch.weight = cleanNumber(data.weight, { min: 0, max: 500 });
  if (data.height !== undefined)          patch.height = cleanNumber(data.height, { min: 0, max: 300 });
  if (data.conditions !== undefined)      patch.conditions = cleanText(data.conditions, FIELD_CAPS.conditions);
  if (data.leaderboardName !== undefined) patch.leaderboardName = cleanText(data.leaderboardName, FIELD_CAPS.leaderboardName);
  if (data.leaderboardOptIn !== undefined) patch.leaderboardOptIn = Boolean(data.leaderboardOptIn);
  if (data.photoURL !== undefined)        patch.photoURL = cleanText(data.photoURL, FIELD_CAPS.url);
  await updateDoc(doc(db, "clients", clientId), patch);
}

export async function uploadClientPhoto(_clientId, file) {
  // Store at clientPhotos/{auth.uid} — only the client uploads their own photo,
  // so the uploader's auth UID is both the path segment and the rule check.
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Not authenticated");
  const storageRef = ref(storage, `clientPhotos/${uid}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

// -------- PROGRESS MEASUREMENTS --------
// Stored in clients/{clientId}/measurements/{autoId}.
// Each doc: { date (ISO string), weight, bodyFat, neck, waist, chest, hips, arms, thighs, createdAt }
const MEASUREMENT_BOUNDS = {
  weight:  { min: 0, max: 500 },   // kg
  bodyFat: { min: 0, max: 100 },   // %
  neck:    { min: 0, max: 100 },   // cm
  waist:   { min: 0, max: 250 },
  chest:   { min: 0, max: 250 },
  hips:    { min: 0, max: 250 },
  arms:    { min: 0, max: 150 },
  thighs:  { min: 0, max: 200 },
};

export async function logMeasurement(clientId, data) {
  const clean = {};
  for (const [k, bounds] of Object.entries(MEASUREMENT_BOUNDS)) {
    if (data[k] !== "" && data[k] != null) {
      try {
        clean[k] = cleanNumber(data[k], bounds);
      } catch {
        // Silently skip out-of-range; UI should validate up front but this
        // protects against bad input slipping through.
      }
    }
  }
  // Date must be a plain ISO string, max 40 chars
  const dateRaw = data.date || new Date().toISOString();
  const date = cleanText(dateRaw, 40);
  const ref = await addDoc(collection(db, "clients", clientId, "measurements"), {
    ...clean,
    date,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function deleteMeasurement(clientId, measurementId) {
  await deleteDoc(doc(db, "clients", clientId, "measurements", measurementId));
}

export function subscribeMeasurements(clientId, cb) {
  const q = query(
    collection(db, "clients", clientId, "measurements"),
    orderBy("date", "asc"),
    limit(200)
  );
  return onSnapshot(q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => { console.error("subscribeMeasurements:", err.code); cb([]); }
  );
}

// Before/After progress photos — single pair stored on the client doc.
// Uploading a new one deletes the previous file from Storage first.
export async function uploadProgressPhoto(clientId, slot, file) {
  // slot = "before" | "after"
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Not authenticated");
  const field = slot === "after" ? "afterPhotoURL" : "beforePhotoURL";

  // Delete the existing file (if any) so we never keep history in Storage
  try {
    const snap = await getDoc(doc(db, "clients", clientId));
    const prev = snap.data()?.[field];
    if (prev) {
      const m = prev.match(/\/o\/(.+?)\?/);
      if (m) { try { await deleteObject(ref(storage, decodeURIComponent(m[1]))); } catch {} }
    }
  } catch {}

  const storageRef = ref(storage, `progressPhotos/${clientId}/${slot}`);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  await updateDoc(doc(db, "clients", clientId), { [field]: url });
  return url;
}

export function subscribeCoachSessions(coachId, cb) {
  const q = query(
    collection(db, "sessions"),
    where("coachId", "==", coachId),
    where("coachConfirmed", "==", true),
    where("clientConfirmed", "==", true),
    limit(500)
  );
  return onSnapshot(q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      cb(rows);
    },
    () => cb([])
  );
}

// All sessions where coach has confirmed (clientConfirmed may be true or false).
// Used to compute notifications: unconfirmed (#5) and attendance-based (#2, #3).
export function subscribeAllCoachConfirmedSessions(coachId, cb) {
  const q = query(
    collection(db, "sessions"),
    where("coachId", "==", coachId),
    where("coachConfirmed", "==", true),
    limit(500)
  );
  return onSnapshot(q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => cb([])
  );
}

// -------- CHALLENGES --------
export async function createChallenge(coachId, { title, assignedTo }) {
  const cleanTitle = cleanText(title, FIELD_CAPS.title);
  if (!cleanTitle) throw new Error("Challenge title is required");
  // assignedTo must be an array of client doc IDs; cap to 500 to avoid huge docs
  const cleanAssigned = Array.isArray(assignedTo)
    ? assignedTo.filter((id) => typeof id === "string" && id.length > 0 && id.length <= 128).slice(0, 500)
    : [];
  const ref = await addDoc(collection(db, "challenges"), {
    coachId,
    title: cleanTitle,
    assignedTo: cleanAssigned,
    completedBy: [],
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export function subscribeChallenges(coachId, cb) {
  const q = query(collection(db, "challenges"), where("coachId", "==", coachId));
  return onSnapshot(q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return tb - ta;
      });
      cb(rows);
    },
    (err) => console.error("❌ subscribeChallenges:", err.code)
  );
}

export function subscribeClientChallenges(clientId, coachId, cb) {
  const q = query(
    collection(db, "challenges"),
    where("coachId", "==", coachId),
    where("assignedTo", "array-contains", clientId)
  );
  return onSnapshot(q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      cb(rows);
    },
    (err) => console.error("❌ subscribeClientChallenges:", err.code)
  );
}

export async function markChallengeComplete(challengeId, clientId) {
  const ref = doc(db, "challenges", challengeId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Challenge not found");
  const data = snap.data();
  const completedBy = data.completedBy || [];
  if (!completedBy.includes(clientId)) {
    await updateDoc(ref, { completedBy: [...completedBy, clientId] });
  }
}

export async function deleteChallenge(challengeId) {
  await updateDoc(doc(db, "challenges", challengeId), { deleted: true });
}

// -------- COACH INVITE CODE --------
export function generateCoachCode(uid) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "";
  for (let i = 0; i < 5; i++) {
    code += chars[uid.charCodeAt(i % uid.length) % chars.length];
  }
  return code;
}

export async function getCoachByInviteCode(code) {
  const q = query(
    collection(db, "users"),
    where("role", "==", "coach"),
    where("inviteCode", "==", code.toUpperCase())
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { uid: snap.docs[0].id, ...snap.docs[0].data() };
}

// -------- PENDING CLIENTS --------
export async function createPendingClient(coachId, { name, email, phone, userId, coachingType }) {
  const cleanName = cleanText(name, FIELD_CAPS.name);
  if (!cleanName) throw new Error("Name is required");
  const ref = await addDoc(collection(db, "pendingClients"), {
    coachId,
    name: cleanName,
    email: cleanEmail(email, { required: true }),
    phone: cleanPhone(phone),
    userId: cleanText(userId, 128),
    coachingType: ["live", "self_tracking"].includes(coachingType) ? coachingType : "live",
    status: "pending",
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export function subscribePendingClients(coachId, cb) {
  const q = query(
    collection(db, "pendingClients"),
    where("coachId", "==", coachId),
    where("status", "==", "pending")
  );
  return onSnapshot(q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return ta - tb;
      });
      cb(rows);
    },
    (err) => console.error("❌ subscribePendingClients:", err.code)
  );
}

export async function acceptPendingClient(pendingId, coachId, payload) {
  console.log("Step 1: Reading pending doc", pendingId);
  const pendingSnap = await getDoc(doc(db, "pendingClients", pendingId));
  const pendingData = pendingSnap.exists() ? pendingSnap.data() : null;
  const clientUserId = pendingData?.userId || null;
  const pendingCoachingType = pendingData?.coachingType || "live";
  console.log("Step 1 done, clientUserId:", clientUserId);

  console.log("Step 2: Adding client");
  // Pass userId directly — avoids querying users collection
  // coachingType: prefer form value if set, otherwise inherit from pending record
  const clientId = await addClient(coachId, {
    ...payload,
    userId: clientUserId,
    coachingType: payload.coachingType || pendingCoachingType,
  });
  console.log("Step 2 done, clientId:", clientId);

  console.log("Step 3: Updating pending status");
  try {
    await updateDoc(doc(db, "pendingClients", pendingId), {
      status: "accepted",
      clientId: clientId,
    });
    console.log("Step 3 done");
  } catch (err) {
    console.error("❌ Step 3 failed:", err.code, err.message);
  }

  console.log("Step 4: Linking user account", clientUserId);
  if (clientUserId) {
    try {
      await setDoc(doc(db, "users", clientUserId), {
        clientId: clientId,
      }, { merge: true });
      console.log("Step 4 done");
    } catch (err) {
      console.error("❌ Step 4 failed:", err.code, err.message);
      if (payload.email) {
        try {
          await linkClientByEmail(clientUserId, payload.email);
          console.log("Step 4 fallback done");
        } catch (err2) {
          console.error("❌ Step 4 fallback failed:", err2.code);
        }
      }
    }
  }

  return clientId;
}

export async function dismissPendingClient(pendingId) {
  await updateDoc(doc(db, "pendingClients", pendingId), { status: "dismissed" });
}

// -------- COACH RATINGS --------
export async function submitCoachRating(coachId, clientId, rating) {
  await setDoc(doc(db, "coachRatings", coachId, "ratings", clientId), {
    rating,
    createdAt: serverTimestamp(),
  });
}

export async function getCoachAverageRating(coachId) {
  const snap = await getDocs(collection(db, "coachRatings", coachId, "ratings"));
  if (snap.empty) return null;
  const total = snap.docs.reduce((sum, d) => sum + (d.data().rating || 0), 0);
  return Math.round((total / snap.docs.length) * 10) / 10;
}

export async function getClientRatingForCoach(coachId, clientId) {
  const snap = await getDoc(doc(db, "coachRatings", coachId, "ratings", clientId));
  return snap.exists() ? snap.data().rating : null;
}

export async function submitSessionRating(sessionId, rating) {
  await updateDoc(doc(db, "sessions", sessionId), {
    sessionRating: rating,
  });
}

export async function updateCoachProfileFull(uid, profileData) {
  // Split: top-level fields that drive currency / billing live on the user
  // doc directly (country, phone). Everything else stays under `profile.*`.
  const { country, phone, ...rest } = profileData || {};
  const topLevel = {};
  if (country !== undefined) topLevel.country = cleanText(country, 2);
  if (phone !== undefined) topLevel.phone = cleanPhone(phone);
  const cleanedProfile = { ...rest };
  if (phone !== undefined) cleanedProfile.phone = cleanPhone(phone); // mirror for display
  if (country !== undefined) cleanedProfile.country = cleanText(country, 2);
  await setDoc(doc(db, "users", uid), { ...topLevel, profile: cleanedProfile }, { merge: true });
}

export async function getCoachPublicProfile(coachId) {
  const snap = await getDoc(doc(db, "users", coachId));
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    uid: coachId,
    name: data.name,
    profile: data.profile || {},
  };
}

// -------- SELF-TRACKING SESSIONS --------

export async function uploadSelfTrackingScreenshot(clientId, file) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Not authenticated");
  const storageRef = ref(storage, `selfTrackingScreenshots/${uid}/${clientId}_${Date.now()}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

export async function logSelfTrackingSession(clientId, coachId, { sessionName, date, time, screenshotUrl }) {
  const docRef = await addDoc(collection(db, "selfTrackingSessions"), {
    clientId,
    coachId,
    sessionName: sessionName || "",
    date: date || "",
    time: time || "",
    screenshotUrl: screenshotUrl || "",
    status: "pending",
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export function subscribeSelfTrackingSessions(clientId, cb) {
  const q = query(
    collection(db, "selfTrackingSessions"),
    where("clientId", "==", clientId),
    limit(100)
  );
  return onSnapshot(q,
    (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      cb(rows);
    },
    (err) => { console.error("subscribeSelfTrackingSessions error:", err); cb([]); }
  );
}

export function subscribeCoachPendingSelfTrackingSessions(coachId, cb) {
  const q = query(
    collection(db, "selfTrackingSessions"),
    where("coachId", "==", coachId),
    where("status", "==", "pending"),
    limit(100)
  );
  return onSnapshot(q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => { console.error("subscribeCoachPendingSelfTrackingSessions error:", err); cb([]); }
  );
}

// All self-tracking sessions regardless of status — used for coach-side
// inactivity, monthly, and rejection-follow-up notifications.
export function subscribeCoachAllSelfTrackingSessions(coachId, cb) {
  const q = query(
    collection(db, "selfTrackingSessions"),
    where("coachId", "==", coachId),
    limit(500)
  );
  return onSnapshot(q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => { console.error("subscribeCoachAllSelfTrackingSessions error:", err); cb([]); }
  );
}

// Parses the storage object path from a Firebase download URL and deletes the file.
// Errors are swallowed so a missing/already-deleted file never blocks confirm/reject.
async function deleteScreenshotByUrl(url) {
  if (!url) return;
  try {
    const match = url.match(/\/o\/(.+?)(?:\?|$)/);
    if (!match) return;
    await deleteObject(ref(storage, decodeURIComponent(match[1])));
  } catch {}
}

export async function confirmSelfTrackingSession(sessionId, clientId, coachId) {
  let screenshotUrl = "";
  await runTransaction(db, async (tx) => {
    const stRef = doc(db, "selfTrackingSessions", sessionId);
    const stSnap = await tx.get(stRef);
    if (!stSnap.exists()) throw new Error("Session not found");
    const stData = stSnap.data();
    screenshotUrl = stData.screenshotUrl || "";

    const clientRef = doc(db, "clients", clientId);
    const clientSnap = await tx.get(clientRef);
    if (!clientSnap.exists()) throw new Error("Client not found");
    const c = clientSnap.data();

    // Parse date + time from the self-tracking submission so heatmap/stats are accurate
    let sessionDateTime = Timestamp.now();
    if (stData.date) {
      try {
        const [year, month, day] = stData.date.split("-").map(Number);
        let hour = 0, minute = 0;
        if (stData.time) [hour, minute] = stData.time.split(":").map(Number);
        sessionDateTime = Timestamp.fromDate(new Date(year, month - 1, day, hour, minute));
      } catch {}
    }

    // Mark confirmed and clear the screenshot URL in one write
    tx.update(stRef, { status: "confirmed", screenshotUrl: "" });

    // Create a fully-confirmed entry in the regular sessions collection
    // (leaderboard, heatmap, and stats all use this collection — no other changes needed)
    const sessionRef = doc(collection(db, "sessions"));
    tx.set(sessionRef, {
      clientId,
      coachId,
      dateTime: sessionDateTime,
      notes: stData.sessionName || "",
      status: "attended",
      coachConfirmed: true,
      clientConfirmed: true,
      source: "self_tracking",
      selfTrackingSessionId: sessionId,
      createdAt: serverTimestamp(),
    });

    // Increment sessionsUsed
    tx.update(clientRef, { sessionsUsed: (c.sessionsUsed || 0) + 1 });
  });
  // Delete from Storage after the transaction succeeds
  await deleteScreenshotByUrl(screenshotUrl);
}

export async function rejectSelfTrackingSession(sessionId) {
  const sessionRef = doc(db, "selfTrackingSessions", sessionId);
  const snap = await getDoc(sessionRef);
  const screenshotUrl = snap.exists() ? (snap.data().screenshotUrl || "") : "";
  await updateDoc(sessionRef, { status: "rejected", screenshotUrl: "" });
  await deleteScreenshotByUrl(screenshotUrl);
}

// ── ORGANIZATION BRANDING ──────────────────────────────────────────────────
export function subscribeOrganizationBranding(orgId, callback) {
  if (!orgId) { callback(null); return () => {}; }
  return onSnapshot(
    doc(db, "organizations", orgId),
    (snap) => callback(snap.exists() ? snap.data() : null),
    () => callback(null)
  );
}